import {
  inject,
  Component,
  ViewChild,
  DestroyRef,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { deselectObjects } from 'src/app/core/state/selected.actons';
import { ToastService } from '@services/toast.service';
import { faCopy, faFolder } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  DialogCloseOptions,
  DialogCloseReason,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';

import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';
import {
  CompanyApiService,
  CopyWorkspaceParams,
} from 'src/app/api/services/company-api.service';

import { ObjectsData } from '../../../feed/dialogs/feed-duplicate-dialog/feed-duplicate-dialog.component';
import { WorkspaceDuplicateFormComponent } from './workspace-duplicate-form/workspace-duplicate-form.component';

import { TabConfig } from 'src/app/shared/utils';
import {
  DataObject,
  DuplicateObjectApiResponse,
} from 'src/app/core/models/core.models';

import { AppState } from 'src/app/core/state/app.state';

import { forkJoin, tap } from 'rxjs';
import { BusinessConstraintsInfoDialogComponent } from '../../../shared/components/business-constraints-info-dialog/business-constraints-info-dialog.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { DialogTabNavComponent } from '../../../shared/components/dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

interface DialogData extends TabDialogDataBase {
  objectsData: ObjectsData[];
}

interface DialogResult {}

@Component({
  selector: 'app-workspace-duplicate-dialog',
  templateUrl: './workspace-duplicate-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './workspace-duplicate-dialog.component.scss',
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    TranslatePipe,
  ],
})
export class WorkspaceDuplicateDialogComponent extends TabDialogBase<
  DialogData,
  DialogResult
> {
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;
  private utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private companyApi = inject(CompanyApiService);
  private toastService = inject(ToastService);

  objectActionIcon: IconDefinition | IconProp;
  objectIcon: IconDefinition | IconProp;

  objectActionTitle = '';
  objectTitle = '';
  objectTitleDefault = this.translateService.instant(
    'popup.object-title.untitled',
  );

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  objects: DataObject[] = [];
  public workspaceTitles: string = '';

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  // * apply if needed
  get objectsData(): ObjectsData[] {
    return this.dialogData.objectsData;
  }

  ngOnInit(): void {
    this.fetchObjectsById()
      .pipe(
        tap((objects) => {
          this.objects = objects;

          this.setupTabs(objects);

          this.workspaceTitles = this.objects
            .map((obj) => obj.title)
            .join(', ');

          this.objectTitle = this.workspaceTitles;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.setupUIState();
  }

  ngAfterViewInit(): void {
    this.tabSearch.setupAutoSwitch(this.tabs);
  }

  fetchObjectsById() {
    const requests = this.objectsData.map((data) =>
      this.utilsDataObjectApi.fetchObject(data.id, data.classId),
    );

    return forkJoin(requests);
  }

  private setupUIState(): void {
    this.objectActionIcon = faCopy;
    this.objectIcon = faFolder;

    this.objectActionTitle = this.translateService.instant(
      'workspace-duplicate-popup.tab.copy-workspace',
    );
  }

  private setupTabs(objects): void {
    this.tabs.push({
      id: 'copy-workspace',
      label: this.translateService.instant(
        'workspace-duplicate-popup.tab.copy-workspace',
      ),
      icon: ['far', 'clone'],
      component: WorkspaceDuplicateFormComponent,
      inputs: {
        objects,
      },
    });

    this.initializeBase();
  }

  protected override performSave(
    formData: any,
  ): DialogCloseOptions<DialogResult> {
    const copyParams: CopyWorkspaceParams = {
      source_workspace_id: formData.source_workspace_id,
      target_company_id: formData.target_company_id,
      target_user_id: formData.target_user_id,
      copy_only_selected: formData.copy_only_selected,
      feed_ids: formData.feed_ids,
      dashboard_ids: formData.dashboard_ids,
    };

    this.companyApi.copyCompanyWorkspace(copyParams).subscribe({
      next: (response: DuplicateObjectApiResponse) => {
        if ('success' in response) {
          this.toastService.showSuccess(
            this.translateService.instant(
              'workspace-duplicate-popup.message.duplicate-workspace-success',
            ),
          );
          // Sources are done with; a constraint error or a transport failure leaves
          // the selection in place.
          this.store.dispatch(
            deselectObjects({ objectIds: copyParams.source_workspace_id }),
          );
        } else {
          const errors = Object.values(response.errors).sort(
            (a, b) => a.display_order - b.display_order,
          );

          this.dialogService.openSimpleDialog(
            BusinessConstraintsInfoDialogComponent,
            {
              data: {
                errors,
              },
              closeOnClick: false,
            },
          );
        }
      },
      error: (error) => {
        this.translateService.instant(
          'workspace-duplicate-popup.message.duplicate-workspace-error-generic',
        );
      },
    });

    return { reason: DialogCloseReason.Save, data: formData };
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    const component = this.tabManager.getTabComponent('copy-workspace');

    if (component?.getFormData()) {
      const formData = component.getFormData();
      const result = this.performSave(formData);
      this.dirtyHandler.closeImmediately(result);
    }
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
